import React, { useMemo, useState, useCallback } from 'react';
import { Box } from '@material-ui/core';
import CarwizDataGrid from '../../../../components/CarwizDataGrid';
import gridLocaleText from '../../../UICache/gridLocaleText';
import { useCsvData } from './hooks/useCsvData';
import { useCsvEditor } from './hooks/useCsvEditor';
import { CsvEditorProvider } from './contexts/CsvEditorContext';
import { COLUMNS } from './constants';
import LoadingOverlay from './components/LoadingOverlay';
import ActionButtons from './components/ActionButtons';
import FilterBar from './components/FilterBar';
import { CsvEditorStyles } from './components/CsvEditorStyles';

const CsvEditorContent = ({ uploadCsvString, syncBqToPgNames }) => {
  // State to track final filtered rows from CarwizDataGrid
  const [finalFilteredRows, setFinalFilteredRows] = useState(null);

  // Callback to receive filtered rows from CarwizDataGrid
  const handleFilteredRowsChange = useCallback((filteredRows) => {
    setFinalFilteredRows(filteredRows);
  }, []);

  // Master hook that composes all sub-hooks
  const {
    rows,
    filteredRows,
    isLoading,
    isSaving,
    showOnlyChanged,
    setShowOnlyChanged,
    changedRowsCount,
    page,
    pageSize,
    canNavigateNext,
    canNavigatePrevious,
    isFiltersFixed,
    gridRightEdge,
    gridContainerRef,
    handleCellEditCommit,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    handleNextMissing,
    handlePreviousMissing,
    handleSave,
    getRowClassName
  } = useCsvEditor(uploadCsvString, syncBqToPgNames, 50, finalFilteredRows);

  // Columns - memoized for performance (prevents recreating on every render)
  const columns = useMemo(() => COLUMNS(rows), [rows]);

  return (
    <div ref={gridContainerRef} style={{ width: '100%', height: '100%' }}>
      <CsvEditorStyles />
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap'
        }}
      >
        <FilterBar
          showOnlyChanged={showOnlyChanged}
          setShowOnlyChanged={setShowOnlyChanged}
          changedRowsCount={changedRowsCount}
          canNavigatePrevious={canNavigatePrevious}
          canNavigateNext={canNavigateNext}
          handlePreviousMissing={handlePreviousMissing}
          handleNextMissing={handleNextMissing}
          isLoading={isLoading}
          isSaving={isSaving}
          isFiltersFixed={isFiltersFixed}
          gridRightEdge={gridRightEdge}
        />
        <ActionButtons
          handleSave={handleSave}
          isSaving={isSaving}
          isLoading={isLoading}
          changedRowsCount={changedRowsCount}
        />
      </Box>
      <CarwizDataGrid
        key={`${showOnlyChanged}`}
        rows={filteredRows}
        columns={columns}
        loading={isLoading || isSaving}
        disabled={isSaving}
        onCellEditCommit={handleCellEditCommit}
        getRowClassName={getRowClassName}
        onRowClick={handleRowClick}
        onFilteredRowsChange={handleFilteredRowsChange}
        disableColumnSelector
        disableSelectionOnClick
        disableColumnResize
        autoHeight
        page={page}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPageOptions={[25, 50, 100]}
        pagination
        components={{
          LoadingOverlay
        }}
        componentsProps={{
          loadingOverlay: {}
        }}
        style={{
          minHeight: '200px',
          pointerEvents: isSaving ? 'none' : 'auto',
          opacity: isSaving ? 0.6 : 1
        }}
        localeText={{
          ...gridLocaleText,
          MuiTablePagination: {
            backIconButtonText: 'עמוד הקודם',
            labelRowsPerPage: 'שורות לעמוד:',
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} מתוך ${count !== -1 ? count : `יותר מ-${to}`}`,
            nextIconButtonText: 'עמוד הבא'
          }
        }}
      />
    </div>
  );
};

const CsvEditor = ({ downloadCsvString, uploadCsvString, syncBqToPgNames }) => {
  // Load CSV data
  const csvData = useCsvData(downloadCsvString);

  return (
    <CsvEditorProvider value={csvData}>
      <CsvEditorContent
        uploadCsvString={uploadCsvString}
        syncBqToPgNames={syncBqToPgNames}
      />
    </CsvEditorProvider>
  );
};

export default CsvEditor;
